import Model, { attr, belongsTo } from '@ember-data/model';
import { cached } from '@glimmer/tracking';
import { parseISO } from 'date-fns/parseISO';
import type { ModelRegistry } from 'ember-data/types/registries/model';

export enum ChangeoverStatus {
  started = 'started',
  finished = 'finished',
  pending = 'pending',
  accepted = 'accepted',
}

export default class ChangeoverModel extends Model {
  @attr('string') persistedTeammateId?: string;
  @attr('string') cleanerName!: string;
  @attr('string') hostNote!: string;
  @attr('string') status!: ChangeoverStatus;
  @attr('string') scheduledAt!: string;
  @attr('string') taskId?: string;

  @cached
  get scheduledAtInUTC(): Date {
    return parseISO(this.scheduledAt.slice(0, 10));
  }

  @cached
  get bookingEndDate(): Date {
    return this.booking.endDate;
  }

  @cached
  get displayName(): string {
    return this.booking?.displayName ?? '';
  }

  @cached
  get isPending(): boolean {
    return this.status === ChangeoverStatus.pending;
  }

  @cached
  get isAccepted(): boolean {
    return this.status === ChangeoverStatus.accepted;
  }

  @cached
  get isStarted(): boolean {
    return this.status === ChangeoverStatus.started;
  }

  @cached
  get isFinished(): boolean {
    return this.status === ChangeoverStatus.finished;
  }

  @belongsTo('booking', { inverse: 'changeover', async: false })
  booking!: ModelRegistry['booking'];

  @belongsTo('property', { inverse: null, async: false })
  property!: ModelRegistry['property'];

  @belongsTo('calendar', { inverse: 'changeovers', async: false })
  calendar!: ModelRegistry['calendar'];

  @belongsTo('teammate', { inverse: null, async: false })
  teammate?: ModelRegistry['teammate'];

  @belongsTo('booking', { inverse: null, async: false })
  nextBooking?: ModelRegistry['booking'];

  @cached
  get persistedTeammate(): ModelRegistry['teammate'] | undefined {
    if (this.persistedTeammateId) {
      return this.store.peekRecord('teammate', this.persistedTeammateId);
    }

    return this.teammate;
  }
}

declare module 'ember-data/types/registries/model' {
  interface ModelRegistry {
    changeover: ChangeoverModel;
  }
}
